import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Organizer-only "Add Match" — opened from a specific round's "+" icon (see
 * RoundSection.jsx), so `round` is already fixed; this only picks the two
 * participants. Options come from `fetchAllParticipants()` (the same
 * provider the wizard itself used — real enrolled teams/players, or mock
 * ones in Explore mode), fetched fresh each time the modal opens rather than
 * reusing anything cached, since a participant could have been added/removed
 * since the schedule was generated. `entityType` mirrors the old tab's own
 * 'team'/'user' values — the only two `participant_a_type`/`participant_b_type`
 * the backend accepts.
 *
 * Portaled to document.body, same reasoning as MatchSettingsModal.jsx: the
 * "+" icon that opens this lives deep inside GeneratedScheduleStep's
 * `tf-animate-in` wrapper, whose animation touches `transform` and leaves a
 * non-`none` computed value even after finishing — that traps
 * `position: fixed` descendants inside the page's own scrolled content
 * instead of the true viewport, breaking centering. `stopPropagation` on the
 * outer div is separate and still needed even though nothing here currently
 * sits inside a clickable row/round header — cheap insurance against the
 * same bubbling bug if that ever changes.
 */
const AddMatchModal = ({ isOpen, onClose, round, participantType, fetchAllParticipants, onSave }) => {
  const { theme } = useTheme();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [participantAId, setParticipantAId] = useState('');
  const [participantBId, setParticipantBId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const entityType = participantType === 'teams' ? 'team' : 'user';
  const entityLabel = participantType === 'teams' ? 'Team' : 'Player';

  useEffect(() => {
    if (!isOpen) return;
    setParticipantAId('');
    setParticipantBId('');
    setError(null);
    setLoading(true);
    fetchAllParticipants()
      .then(setParticipants)
      .finally(() => setLoading(false));
  }, [isOpen, fetchAllParticipants]);

  if (!isOpen) return null;

  const close = () => {
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!participantAId || !participantBId) return;
    setIsSaving(true);
    setError(null);
    try {
      await onSave(round, {
        participantAType: entityType,
        participantAId: Number(participantAId),
        participantBType: entityType,
        participantBId: Number(participantBId),
      });
      close();
    } catch (e) {
      setError(e.message || 'Failed to create match');
      setIsSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
      <div className={`relative w-full max-w-md rounded-2xl border ${theme.border} ${theme.card} ${theme.shadow} p-6`}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className={`text-lg font-bold ${theme.text}`}>Add New Match</h3>
          <button
            type="button"
            onClick={close}
            className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${theme.buttonGhost}`}
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={`mb-2 block text-sm font-medium ${theme.text}`}>{entityLabel} A *</label>
            <select
              value={participantAId}
              onChange={(e) => setParticipantAId(e.target.value)}
              disabled={loading}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${theme.input}`}
            >
              <option value="">Select {entityLabel}</option>
              {participants
                .filter((p) => String(p.id) !== String(participantBId))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${theme.text}`}>{entityLabel} B *</label>
            <select
              value={participantBId}
              onChange={(e) => setParticipantBId(e.target.value)}
              disabled={loading}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${theme.input}`}
            >
              <option value="">Select {entityLabel}</option>
              {participants
                .filter((p) => String(p.id) !== String(participantAId))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </div>

          {round && (
            <div className={`rounded-lg p-3 ${theme.backgroundSecondary}`}>
              <p className={`text-sm ${theme.textMuted}`}>Adding to {round.label}</p>
            </div>
          )}

          {error && <p className="text-xs font-medium text-red-500">{error}</p>}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={isSaving}
            className={`rounded-lg border px-4 py-2 text-sm font-medium ${theme.border} ${theme.text} ${theme.buttonGhost} disabled:opacity-50`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!participantAId || !participantBId || isSaving}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {isSaving ? 'Creating…' : 'Create Match'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddMatchModal;
